import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { rosterAPI, clientAPI, userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, ChevronLeft, ChevronRight, Calendar, Clock, Users, MapPin,
  Briefcase, BookOpen, Palmtree, AlertTriangle, Trash2, Edit, Building2,
} from 'lucide-react';

const ENTRY_TYPES = [
  { value: 'client_visit', label: 'Client Visit', color: 'bg-blue-100 text-blue-700', icon: MapPin },
  { value: 'visit', label: 'Visit', color: 'bg-blue-100 text-blue-700', icon: MapPin },
  { value: 'internal', label: 'Internal', color: 'bg-slate-100 text-slate-700', icon: Briefcase },
  { value: 'training', label: 'Training', color: 'bg-purple-100 text-purple-700', icon: BookOpen },
  { value: 'wfh', label: 'WFH', color: 'bg-emerald-100 text-emerald-700', icon: Briefcase },
  { value: 'leave', label: 'Leave', color: 'bg-amber-100 text-amber-700', icon: Palmtree },
];

const getEntryType = (type) => ENTRY_TYPES.find(e => e.value === type) || ENTRY_TYPES[0];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const RosterPage = () => {
  const { user, isAdmin } = useAuth();
  usePageTitle('Roster');
  const [entries, setEntries] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [summary, setSummary] = useState(null);
  const [clientSummary, setClientSummary] = useState(null);
  const [viewMode, setViewMode] = useState('calendar');
  const [clientFilter, setClientFilter] = useState('all');

  // Dialogs
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [conflictWarning, setConflictWarning] = useState('');
  const [dayDetailsOpen, setDayDetailsOpen] = useState(null); // {date, entries}

  // Form
  const [form, setForm] = useState({
    consultant_id: '', client_id: '', date: '', start_time: '09:00',
    end_time: '17:00', entry_type: 'client_visit', title: '', notes: '',
  });
  const [isDateRange, setIsDateRange] = useState(false);
  const [endDate, setEndDate] = useState('');

  const loadData = useCallback(async () => {
    try {
      const params = { month: currentMonth };
      if (viewMode === 'client' && clientFilter !== 'all') {
        params.client_id = clientFilter;
      }
      const [entriesRes, summaryRes, clientSummaryRes] = await Promise.all([
        rosterAPI.getAll(params),
        isAdmin() ? rosterAPI.getMonthlySummary(currentMonth) : Promise.resolve({ data: null }),
        isAdmin() ? rosterAPI.getClientSummary(currentMonth, clientFilter !== 'all' ? clientFilter : undefined) : Promise.resolve({ data: null }),
      ]);
      setEntries(entriesRes.data);
      if (summaryRes.data) setSummary(summaryRes.data);
      if (clientSummaryRes.data) setClientSummary(clientSummaryRes.data);
    } catch {
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [currentMonth, isAdmin, viewMode, clientFilter]);

  const loadDropdowns = useCallback(async () => {
    try {
      const [clientsRes, staffRes] = await Promise.all([
        clientAPI.getAll(),
        userAPI.getAll({ role: 'staff' }).catch(() => ({ data: [] })),
      ]);
      setClients(clientsRes.data || []);
      const allStaff = staffRes.data || [];
      setConsultants(allStaff.filter(u => ['staff', 'admin', 'super_admin'].includes(u.role)));
    } catch { /* ok */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (isAdmin()) loadDropdowns(); }, [isAdmin, loadDropdowns]);

  const navigateMonth = (dir) => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const getMonthLabel = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    return new Date(y, m - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  const getCalendarDays = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1).getDay();
    const daysInMonth = new Date(y, m, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const getEntriesForDate = (day) => {
    if (!day) return [];
    const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
    let filtered = entries.filter(e => e.date === dateStr);
    if (viewMode === 'client' && clientFilter !== 'all') {
      filtered = filtered.filter(e => e.client_id === clientFilter);
    }
    return filtered;
  };

  const openCreateForDate = (day) => {
    if (!isAdmin()) return;
    const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
    const defaults = { consultant_id: '', client_id: '', date: dateStr, start_time: '09:00', end_time: '17:00', entry_type: 'client_visit', title: '', notes: '' };
    if (viewMode === 'client' && clientFilter !== 'all') {
      defaults.client_id = clientFilter;
      defaults.entry_type = 'client_visit';
    }
    setForm(defaults);
    setConflictWarning('');
    setIsDateRange(false);
    setEndDate('');
    setIsCreateOpen(true);
  };

  const checkConflicts = async (consultantId, dateStr) => {
    if (!dateStr || !consultantId) { setConflictWarning(''); return; }
    try {
      const res = await rosterAPI.checkConflicts(consultantId, dateStr);
      if (res.data.on_leave) {
        setConflictWarning(`This consultant has approved leave on this date (${res.data.leave?.leave_type})`);
      } else if (res.data.is_busy) {
        const names = res.data.existing_entries.map(e => e.title || e.entry_type).join(', ');
        setConflictWarning(`This consultant already has entries on this date: ${names}`);
      } else {
        setConflictWarning('');
      }
    } catch { /* ok */ }
  };

  const handleConsultantChange = async (consultantId) => {
    setForm(f => ({ ...f, consultant_id: consultantId }));
    await checkConflicts(consultantId, form.date);
  };

  const handleDateChange = async (dateVal) => {
    setForm(f => ({ ...f, date: dateVal }));
    await checkConflicts(form.consultant_id, dateVal);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.consultant_id || !form.date) {
      toast.error('Consultant and date are required');
      return;
    }
    try {
      if (isDateRange && endDate && endDate >= form.date) {
        // Bulk create for date range
        const res = await rosterAPI.bulkCreate({
          consultant_id: form.consultant_id,
          client_id: form.client_id || null,
          start_date: form.date,
          end_date: endDate,
          start_time: form.start_time,
          end_time: form.end_time,
          entry_type: form.entry_type,
          title: form.title,
          notes: form.notes,
        });
        if (res.data.warnings?.length > 0) {
          res.data.warnings.forEach(w => toast.warning(w));
        }
        toast.success(`${res.data.total_created} roster entries created${res.data.visits_created ? ` (${res.data.visits_created} visits synced)` : ''}`);
      } else {
        // Single date
        const res = await rosterAPI.create(form);
        if (res.data.conflict_warning) toast.warning(res.data.conflict_warning);
        if (res.data.linked_visit_id) {
          toast.success('Roster entry created & synced to Visit Management');
        } else {
          toast.success('Roster entry created');
        }
      }
      setIsCreateOpen(false);
      setIsDateRange(false);
      setEndDate('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    }
  };

  const openEdit = (entry) => {
    setSelectedEntry(entry);
    setForm({
      consultant_id: entry.consultant_id,
      client_id: entry.client_id || '',
      date: entry.date,
      start_time: entry.start_time || '09:00',
      end_time: entry.end_time || '17:00',
      entry_type: entry.entry_type,
      title: entry.title || '',
      notes: entry.notes || '',
    });
    setIsEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await rosterAPI.update(selectedEntry.id, form);
      toast.success('Entry updated');
      setIsEditOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this roster entry? Linked visit will also be removed.')) return;
    try {
      await rosterAPI.delete(id);
      toast.success('Entry deleted');
      loadData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const calendarDays = getCalendarDays();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  if (loading) return <div className="flex items-center justify-center h-64"><Clock className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="roster-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-600 via-blue-500 to-indigo-500 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Consultant Roster</h1>
            <p className="text-sm text-cyan-100">Schedule visits, manage availability & client-wise planning</p>
          </div>
          <div className="flex gap-2">
            <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
              <Button variant={viewMode === 'calendar' ? 'default' : 'ghost'} size="sm" onClick={() => { setViewMode('calendar'); setClientFilter('all'); }} className={viewMode !== 'calendar' ? 'text-white hover:bg-white/20' : ''} data-testid="view-calendar">
                <Calendar className="w-4 h-4 mr-1" /> Internal
              </Button>
              <Button variant={viewMode === 'client' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('client')} className={viewMode !== 'client' ? 'text-white hover:bg-white/20' : ''} data-testid="view-client">
                <Building2 className="w-4 h-4 mr-1" /> Client Roster
              </Button>
              <Button variant={viewMode === 'summary' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('summary')} className={viewMode !== 'summary' ? 'text-white hover:bg-white/20' : ''} data-testid="view-summary">
                <Users className="w-4 h-4 mr-1" /> Summary
              </Button>
            </div>
            {isAdmin() && (
              <Button onClick={() => { setForm({ consultant_id: '', client_id: viewMode === 'client' && clientFilter !== 'all' ? clientFilter : '', date: '', start_time: '09:00', end_time: '17:00', entry_type: 'client_visit', title: '', notes: '' }); setConflictWarning(''); setIsCreateOpen(true); }} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="create-roster-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Entry
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)} data-testid="prev-month">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-lg font-semibold" data-testid="current-month">{getMonthLabel()}</h2>
        <Button variant="outline" size="sm" onClick={() => navigateMonth(1)} data-testid="next-month">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Client Filter (visible in Client Roster view) */}
      {viewMode === 'client' && isAdmin() && (
        <div className="flex items-center gap-3">
          <Label className="text-sm font-medium">Select Client:</Label>
          <Select value={clientFilter} onValueChange={(v) => setClientFilter(v)}>
            <SelectTrigger className="w-80" data-testid="client-filter-select">
              <Building2 className="w-4 h-4 mr-2" /><SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {ENTRY_TYPES.map(et => (
          <div key={et.value} className="flex items-center gap-1.5 text-xs">
            <span className={`inline-block w-3 h-3 rounded ${et.color.split(' ')[0]}`} />
            <span className="text-muted-foreground">{et.label}</span>
          </div>
        ))}
      </div>

      {(viewMode === 'calendar' || viewMode === 'client') ? (
        /* Calendar View (Internal or Client) */
        <Card>
          {viewMode === 'client' && clientFilter !== 'all' && (
            <CardHeader className="pb-2 border-b bg-blue-50/50">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-base text-blue-800">
                  {clients.find(c => c.id === clientFilter)?.organization_name || 'Client'} - Schedule
                </CardTitle>
              </div>
            </CardHeader>
          )}
          <CardContent className="p-0">
            <div className="grid grid-cols-7 border-b">
              {DAYS.map(d => (
                <div key={d} className="p-2 text-center text-xs font-medium text-muted-foreground border-r last:border-r-0">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {calendarDays.map((day, idx) => {
                const dateStr = day ? `${currentMonth}-${String(day).padStart(2, '0')}` : '';
                const dayEntries = day ? getEntriesForDate(day) : [];
                const isToday = dateStr === todayStr;
                return (
                  <div
                    key={idx}
                    className={`min-h-[120px] border-r border-b last:border-r-0 p-1 transition-colors ${
                      day ? 'hover:bg-muted/30 cursor-pointer' : 'bg-muted/10'
                    } ${isToday ? 'bg-blue-50/50' : ''}`}
                    onClick={() => day && openCreateForDate(day)}
                    data-testid={day ? `calendar-day-${day}` : undefined}
                  >
                    {day && (
                      <>
                        <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600 font-bold' : 'text-muted-foreground'}`}>{day}</div>
                        <div className="space-y-0.5">
                          {dayEntries.slice(0, 3).map(entry => {
                            const et = getEntryType(entry.entry_type);
                            return (
                              <div
                                key={entry.id}
                                className={`text-[10px] px-1 py-0.5 rounded truncate ${et.color}`}
                                onClick={(e) => { e.stopPropagation(); openEdit(entry); }}
                                title={`${entry.consultant_name}: ${entry.title || entry.entry_type}${entry.client_name ? ` @ ${entry.client_name}` : ''}`}
                                data-testid={`roster-entry-${entry.id}`}
                              >
                                {viewMode === 'client'
                                  ? `${entry.consultant_name?.split(' ')[0]} ${entry.start_time || ''}`
                                  : `${entry.consultant_name?.split(' ')[0]} - ${entry.title || et.label}`
                                }
                              </div>
                            );
                          })}
                          {dayEntries.length > 3 && (
                            <button
                              type="button"
                              className="text-[10px] text-blue-600 hover:underline pl-1 font-medium"
                              onClick={(e) => { e.stopPropagation(); setDayDetailsOpen({ date: dateStr, entries: dayEntries }); }}
                              data-testid={`more-entries-${dateStr}`}
                            >+{dayEntries.length - 3} more</button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Summary View */
        <div className="space-y-4">
          {/* Consultant Summary */}
          {summary?.consultants?.length > 0 ? (
            summary.consultants.map(c => (
              <Card key={c.consultant_id} data-testid={`summary-consultant-${c.consultant_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{c.consultant_name}</CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="text-xs"><MapPin className="w-3 h-3 mr-1" /> {c.total_visits} visits</Badge>
                      <Badge variant="outline" className="text-xs"><Briefcase className="w-3 h-3 mr-1" /> {c.total_internal} internal</Badge>
                      <Badge variant="outline" className="text-xs"><Palmtree className="w-3 h-3 mr-1" /> {c.total_leave} leave</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {c.entries.map(entry => {
                      const et = getEntryType(entry.entry_type);
                      const Icon = et.icon;
                      return (
                        <div key={entry.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-muted/50 group">
                          <div className="flex items-center gap-3">
                            <Badge className={`${et.color} text-[10px]`}><Icon className="w-3 h-3 mr-1" /> {et.label}</Badge>
                            <span className="text-sm font-medium">{entry.date}</span>
                            <span className="text-sm text-muted-foreground">{entry.title || '-'}</span>
                            {entry.client_name && <span className="text-xs text-blue-600">@ {entry.client_name}</span>}
                            {entry.start_time && <span className="text-xs text-muted-foreground">{entry.start_time} - {entry.end_time}</span>}
                          </div>
                          {isAdmin() && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(entry)}><Edit className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(entry.id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No roster entries for {getMonthLabel()}</CardContent></Card>
          )}

          {/* Client Summary (below consultant summary) */}
          {clientSummary?.clients?.length > 0 && (
            <>
              <h3 className="text-lg font-semibold mt-6">Client-wise Summary</h3>
              {clientSummary.clients.map(c => (
                <Card key={c.client_id} data-testid={`client-summary-${c.client_id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <CardTitle className="text-base">{c.client_name}</CardTitle>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant="outline" className="text-xs"><MapPin className="w-3 h-3 mr-1" /> {c.total_visits} visits</Badge>
                        <Badge variant="outline" className="text-xs"><Calendar className="w-3 h-3 mr-1" /> {c.unique_days} days</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {c.entries.map(entry => (
                        <div key={entry.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-muted/50 group">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">{entry.date}</span>
                            <Badge variant="outline" className="text-xs">{entry.consultant_name}</Badge>
                            <span className="text-sm text-muted-foreground">{entry.title || '-'}</span>
                            {entry.start_time && <span className="text-xs text-muted-foreground">{entry.start_time} - {entry.end_time}</span>}
                          </div>
                          {isAdmin() && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(entry)}><Edit className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(entry.id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {/* Create Entry Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Roster Entry</DialogTitle>
            <DialogDescription>Schedule a consultant visit or activity</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Consultant *</Label>
              <Select value={form.consultant_id} onValueChange={handleConsultantChange}>
                <SelectTrigger data-testid="roster-form-consultant"><SelectValue placeholder="Select consultant" /></SelectTrigger>
                <SelectContent>{consultants.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Date *</Label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={isDateRange} onChange={e => { setIsDateRange(e.target.checked); if (!e.target.checked) setEndDate(''); }} className="rounded border-gray-300" />
                  <span className="text-xs text-muted-foreground">Date Range (multi-day)</span>
                </label>
              </div>
              {isDateRange ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-xs text-muted-foreground">From</span>
                    <Input type="date" value={form.date} onChange={e => handleDateChange(e.target.value)} required data-testid="roster-form-start-date" />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">To</span>
                    <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={form.date} required data-testid="roster-form-end-date" />
                  </div>
                  {form.date && endDate && endDate >= form.date && (
                    <p className="col-span-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
                      {Math.floor((new Date(endDate) - new Date(form.date)) / 86400000) + 1} days will be scheduled ({form.date} to {endDate})
                    </p>
                  )}
                </div>
              ) : (
                <Input type="date" value={form.date} onChange={e => handleDateChange(e.target.value)} required data-testid="roster-form-date" />
              )}
            </div>
            {conflictWarning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">{conflictWarning}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.entry_type} onValueChange={v => setForm(f => ({ ...f, entry_type: v }))}>
                <SelectTrigger data-testid="roster-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTRY_TYPES.map(et => <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.entry_type === 'client_visit' && (
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={form.client_id} onValueChange={v => setForm(f => ({ ...f, client_id: v }))}>
                  <SelectTrigger data-testid="roster-form-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-blue-600">Client visits are auto-synced to Visit Management</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Optional title" data-testid="roster-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Time</Label><Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} /></div>
              <div className="space-y-2"><Label>End Time</Label><Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} /></div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Optional notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="roster-form-submit">Create Entry</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Roster Entry</DialogTitle>
            <DialogDescription>Update schedule entry</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.entry_type} onValueChange={v => setForm(f => ({ ...f, entry_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ENTRY_TYPES.map(et => <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.entry_type === 'client_visit' && (
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={v => setForm(f => ({ ...f, client_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Time</Label><Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} /></div>
              <div className="space-y-2"><Label>End Time</Label><Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} /></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
            <DialogFooter>
              {isAdmin() && selectedEntry && (
                <Button type="button" variant="destructive" onClick={() => { handleDelete(selectedEntry.id); setIsEditOpen(false); }} className="mr-auto" data-testid="roster-delete-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="roster-edit-submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Day Details Dialog (when +N more is clicked) */}
      <Dialog open={!!dayDetailsOpen} onOpenChange={() => setDayDetailsOpen(null)}>
        <DialogContent className="max-w-md" data-testid="day-details-dialog">
          <DialogHeader>
            <DialogTitle>All Entries — {dayDetailsOpen?.date}</DialogTitle>
            <DialogDescription>{dayDetailsOpen?.entries?.length || 0} entries scheduled for this day</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {dayDetailsOpen?.entries?.map(entry => {
              const et = getEntryType(entry.entry_type);
              return (
                <button
                  key={entry.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 rounded border hover:ring-2 hover:ring-blue-300 transition ${et.color}`}
                  onClick={() => { setDayDetailsOpen(null); openEdit(entry); }}
                  data-testid={`day-detail-entry-${entry.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{entry.consultant_name} — {entry.title || et.label}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {entry.start_time && `${entry.start_time}${entry.end_time ? ` - ${entry.end_time}` : ''} • `}
                        {entry.client_name || ''}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{et.label}</Badge>
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDayDetailsOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RosterPage;
